import AddIcon from '@mui/icons-material/Add';
import DeleteButton from '../components/DeleteButton';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { Avatar, Box, Button, Divider, Grid, IconButton, InputAdornment, ListItem, ListItemAvatar, ListItemText, TextField, Toolbar } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router';

import { DataAPIContext } from '../api';
import { UPDATE_BATCH } from '../api/dataProvider';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import images from '../components/images';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import { debounce } from '../utils';
import { useIsWidthUp } from '../utils';
import FilterEdit from './FilterEdit';
import { ConfirmationDialogText } from './utils';

const useToolbarStyles = makeStyles((theme) => ({
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
    rightButton: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
}));


const FiltersListToolbar = (props) => {
    const createBt = React.useRef(null);
    const deleteBt = React.useRef(null);
    const history = useHistory();
    const { hasPerm } = React.useContext(ContextStore);

    const searchQueryRef = React.useRef(null);
    const btSize = props.isSmallScreen ? 'medium' : 'small';
    const classes = useToolbarStyles();

    const handleSearchQueryChange = (ev) => {
        debouncedSearchQueryChangeHandler.cancel();
        doSearch(searchQueryRef.current?.value);
    };

    const onCreateNewFilterBtClick = (ev) => {
        history.push('/explore/filters/create');
    };

    const onDeleteFilterBtClick = (ev) => {
        props.onDeleteFilter && props.onDeleteFilter(props.selectedIds);
    };

    const debouncedSearchQueryChangeHandler = debounce(handleSearchQueryChange, 300);

    const doSearch = (query) => props.onQueryChange && props.onQueryChange(query);

    return (
        <Toolbar disableGutters={true} sx={{ minHeight: 0 }}>
            <Grid container direction="row" spacing={1}>
                <Grid item xs={9} sm={4} container alignItems="center" justifyContent="flex-start">
                    <TextField
                        variant="filled"
                        label="Search"
                        size="small"
                        onChange={debouncedSearchQueryChangeHandler}
                        inputRef={searchQueryRef}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton aria-label="do search" onClick={handleSearchQueryChange}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={3} sm={8} container justifyContent="flex-end" alignItems="center">
                    {props.selectedIds && props.selectedIds.length > 0 ? (
                        <Box mr={2}>
                            <DeleteButton ref={deleteBt} perm="trip_type.delete" size={btSize} onClick={onDeleteFilterBtClick}>
                                Delete filter{props.selectedIds.length > 1 ? 's' : ''}
                            </DeleteButton>
                        </Box>
                    ) : null}
                    {hasPerm('trip_type', 'write') && props.newRowOrder && Object.keys(props.newRowOrder).length ? (
                        <>
                            <Box mr={2}>
                                <Button color="secondary" variant="contained" size={btSize} onClick={(ev) => props.onSaveNewOrder(true)}>
                                    X&nbsp;&nbsp;Cancel
                                </Button>
                            </Box>
                            <Box mr={2}>
                                <Button color="primary" variant="contained" size={btSize} onClick={() => props.onSaveNewOrder()}>
                                    <SaveIcon />
                                    &nbsp;Save new order
                                </Button>
                            </Box>
                        </>
                    ) : null}
                    {!hasPerm('trip_type', 'write') || props.newRowOrder || props.selectedIds?.length > 0 ? null : (
                        <Box direction="row-reverse">
                            <Button ref={createBt} color="primary" variant="contained" size={btSize} onClick={onCreateNewFilterBtClick}>
                                <AddIcon />
                                {!props.isSmallScreen ? ' New filter' : null}
                            </Button>
                        </Box>
                    )}
                </Grid>
            </Grid>
            <div className={classes.grow} />
        </Toolbar>
    );
};


const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: 0,
        margin: theme.spacing(2),
        marginTop: 0,
        marginBottom: 0,
        height: `calc(${theme.cp.contentHeight} - 1rem) !important`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        boxSizing: 'border-box',
    },
    gridRoot: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        borderRadius: '15px',
        paddingBottom: '1rem',
    },
    recordList: {
        height: `calc(${theme.cp.contentHeight} - 6rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    sortButton: {
        minWidth: theme.spacing(4),
        minHeight: theme.spacing(4),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        padding: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    progress: {
        marginBottom: theme.spacing(1),
    },
    listItem: {},
}));

const FiltersList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { settings, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);

    const classes = useStyles();
    const resource = 'trip_type';
    const [query, setQuery] = React.useState(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const [detailsDirty, setDetailsDirty] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [newRowOrder, setNewRowOrder] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const isSmallScreen = !useIsWidthUp('sm');
    const history = useHistory();
    const routeParams = useParams();
    const [activeFilterId, setActiveFilterId] = React.useState(routeParams.id);
    const imageHeight = 48;
    const imageWidth = 48;

    React.useEffect(() => {
        if (!routeParams.id) {
            setActiveFilterId(null);
        }
    }, [props]);

    const onQueryChange = (query) => {
        setQuery(query);
    };

    const onSelectionModelChange = (ids, selection) => {
        setSelectedIds(selection);
        if (selection.length === 1) {
            history.push(`/explore/filters/${selection[0]}/preview`);
            setActiveFilterId(selection[0]);
        } else {
            history.push(`/explore/filters`);
            setActiveFilterId(null);
        }
    };
    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['filter', 'filters'], onDeleted: refreshList });
    const doDeleteFilterList = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setIdsToDelete(null);
                    history.push(`/explore/filters`);
                }
            })
            .finally(() => {
                setSelectedIds([]);
                setSyncActive(false);
            });
    };

    const onDeleteFilterList = (idlist) => {
        setIdsToDelete(idlist);
        setConfirmationDialogAction(idlist.length > 1 ? 'delete_filters' : 'delete_filter');
    };

    const onFilterDelete = (f) => {
        return doDeleteFilterList([f.id]);
    };

    const onRowOrderUpdate = async (upd) => {
        if (Object.keys(upd).length === 2) {
            await onSaveNewOrder(false, { ...newRowOrder, ...upd });
            return;
        }
        const newOrder = { ...newRowOrder, ...upd };
        setNewRowOrder(newOrder);
    };

    const onSaveNewOrder = async (reset_order, instantSaveNewOrder) => {
        let rowOrder = newRowOrder || instantSaveNewOrder;
        if (reset_order) {
            setNewRowOrder(null);
            setLastUpdate(new Date().toString());
            enqueueSnackbar('New order canceled', { variant: 'default' });
        } else {
            setSyncActive(true);
            await dataAPI(UPDATE_BATCH, resource, { data: rowOrder, urlSuffix: 'reorder/' })
                .then((res) => {
                    console.log('reorder res', res);
                    if (res.statusCode < 400) {
                        setLastUpdate(new Date().toString());
                        setNewRowOrder(null);
                        enqueueSnackbar('New order saved', { variant: 'success' });
                    } else {
                        enqueueSnackbar('Error saving new order', { variant: 'error' });
                    }
                })
                .catch((err) => {
                    enqueueSnackbar('Error saving new order', { variant: 'error' });
                })
                .finally(() => {
                    setSyncActive(false);
                });
        }
    };

    const onRowClick = (p) => {
        if (!detailsDirty) {
            history.push(`/explore/filters/${p.row.id}/preview`);
            setActiveFilterId(p.row.id);
            setDetailsDirty(false);
        }
    };

    const onDetailsClose = (ev) => {
        setActiveFilterId(null);
        setDetailsDirty(false);
        history.push(`/explore/filters`);
    };

    const onFilterChange = (fn) => {
        setLastUpdate(new Date().toString());
        setDetailsDirty(true);
    };

    const listItemRenderer = (row, idx, rows, gridApi) => {
        const imgUrl = images.url(settings.images.base, row.image, `cover_${imageWidth}x${imageHeight}`);
        return (
            <React.Fragment>
                <ListItem alignItems="flex-start" className={classes.listItem} onClick={(ev) => gridApi.onRowClick({ row, idx }, ev)}>
                    <ListItemAvatar>
                        <Avatar alt={row.name} src={imgUrl} />
                    </ListItemAvatar>

                    <ListItemText primary={row.name} secondary={row.description} />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    const cols = isSmallScreen ? (activeFilterId ? 0 : 12) : activeFilterId ? 8 : 12;

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_filter':
            case 'delete_filters':
                doDeleteFilterList(idsToDelete);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };
    return (
        <div className={classes.root}>
            <PageMeta title="Filters" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            <Grid container direction="row" spacing={2} className={classes.gridRoot} alignItems="stretch">
                {isSmallScreen && activeFilterId ? null : (
                    <Grid
                        item
                        xs={cols > 0 ? cols : false}
                        sx={{
                            height: '100%',
                            pb: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <PageHeading sx={{ mt: 2, mb: 1.5 }}>Filters</PageHeading>
                        <FiltersListToolbar resource={resource} selectedIds={selectedIds} onQueryChange={onQueryChange} onDeleteFilter={onDeleteFilterList} newRowOrder={newRowOrder} onSaveNewOrder={onSaveNewOrder} isSmallScreen={isSmallScreen} />
                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <RecordList
                                className={classes.recordList}
                                resource={resource}
                                disableSelectionOnClick={false}
                                onSelectionModelChange={onSelectionModelChange}
                                onRowClick={onRowClick}
                                deps={lastUpdate}
                                query={query}
                                rowHeight={100}
                                perPage={100}
                                onRowOrderUpdate={onRowOrderUpdate}
                                listItemRenderer={listItemRenderer}
                                disableColumnMenu={true}
                                sortModel={[{ field: 'sequence', sort: 'asc' }]}
                                limitHeightForMobile="100%"
                            // noMatchesLabel={'No Filters'}
                            // createNewButton={<Button style={{ zIndex: 1000 }} variant="contained" onClick={() => history.push('/explore/filters/create')}><AddIcon /> Create new filter</Button>}
                            columns={[
                                ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                                { field: 'sequence', headerName: 'Sequence', type: 'reorder', sortable: false, filter: false },
                                { field: 'name', headerName: 'Name', sortable: false, flex: 1 },
                                { field: 'description', headerName: 'Description', sortable: false, flex: 2.5 },
                                {
                                    field: 'image',
                                    headerName: 'Image',
                                    sortable: false,
                                    flex: 0.5,
                                    valueGetter: (p) => images.url(settings.images.base, p.row.image, `cover_${imageWidth}x${imageHeight}`),
                                    renderCell: (p) => {
                                        return <img alt="img" src={p.value} />;
                                    },
                                },
                                { field: 'custom_id', headerName: 'External ID', sortable: false, flex: 0.5 },
                                { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', ') },
                            ]}
                            />
                        </Box>
                    </Grid>
                )}
                {activeFilterId ? (
                    <Grid item xs={12 - cols} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', pb: 0 }}>
                        <FilterEdit resource={resource} id={activeFilterId} onClose={onDetailsClose} onChange={onFilterChange} onDelete={onFilterDelete} syncActive={syncActive} setSyncActive={setSyncActive} setDetailsDirty={setDetailsDirty} />
                    </Grid>
                ) : null}
            </Grid>
        </div>
    );
};

export default FiltersList;
